/**
 * Login component module.
 * @module investor/shared/components/Login
 *
 */

import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import history from '../../history';
import {_createSession, _openModal} from '../../shared/store';
import {
  Button,
  Column,
  ContactClientServices,
  CopyrightVersion,
  DescriptiveText,
  Image,
  LinkedText,
  Page,
  Row,
  SocialMedia,
  TextInput,
  Tile,
} from '../../shared';
import {withRouter} from 'react-router-dom';
import debug from 'debug';
import styled, {withTheme} from 'styled-components/macro';
import sanitizeHtml from 'sanitize-html';
import {icons} from '../../shared/components/Icon';

const log = debug('circleblack:login');

// TODO KST-1659 Need to fix iframe styling as not cooperating.
const _MediaWrapper = styled.div`
  display: inline-block;
  // height: 100%;
  // width: 100%;
  max-height: 330px;
  max-width: 580px;
  margin: 0px 20px 20px 20px;
`;

const _MediaYouTube = styled.iframe.attrs((props) => ({
  src: props.media,
  frameBorder: 0,
  allow: 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
  allowFullScreen: true,
}))`
  display: block;
  height: 100%;
  width: 100%;
`;

const _MediaVimeo = styled.iframe.attrs((props) => ({
  src: props.media,
  frameBorder: 0,
  allow: 'autoplay; fullscreen',
  allowFullscreen: true,
}))`
  display: block;
  height: 100%;
  width: 100%;
`;

const _Brand = styled(Column)`
  background-color: ${props => props.theme.tileBackgroundColor};
  ${props => props.theme.neumorphic
    ? `box-shadow: 2px 2px 5px 2px rgba(0, 0, 0, 0.4),
                   1px 1px 0 0 rgba(0, 0, 0, 0.5),
                   -1px -1px 0 0 rgba(255, 255, 255, 0.2)` : ''};
`;

/**
 * Login component
 * @name Login
 */
class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
    };
    log('constructed');
  }

  componentDidMount() {
    log('mounting');
    const {session} = this.props;
    if (session.token || localStorage.token) {
      log('pushing / to history');
      history.push('/');
    }
    if (session.email || localStorage.email) {
      log('setting state', {email: session.email || localStorage.email});
      this.setState({email: session.email || localStorage.email});
    }
  }

  getAdvisorMedia = () => {
    const {mediaType, media} = this.props.product && this.props.product.brand;
    switch (mediaType) {
      case 'image':
        return <Image maxHeight='330px' maxWidth='580px' margin='0px 20px 20px 20px' src={media}/>;
      case 'youtube':
        return <_MediaYouTube media={media}/>;
      case 'vimeo':
        return <_MediaVimeo media={media}/>;
      default:
        return '';
    }
  };

  updateValue = (name, value) => this.setState({[name]: value});

  render() {
    log('rendering');
    const {createSession, openModal, product} = this.props;
    const {brand} = product;
    let {email, password} = this.state;
    const enableSocialMedia = brand.facebookLink || brand.twitterLink || brand.linkedInLink || brand.instagramLink;
    const marketingMessage = brand.marketingMsg || brand.media;
    const displayAdvisorMedia = !!(brand.mediaType && brand.media);
    const advisorMedia = this.getAdvisorMedia();
    const isLogoSpaceShared = !!(displayAdvisorMedia || brand.backgroundImage);

    const marketingDisplay = <>
      {!!displayAdvisorMedia && <_MediaWrapper>{advisorMedia}</_MediaWrapper>}
      {!!brand.marketingMsg
      && <DescriptiveText preset='paragraph'
                          alignment='center'
                          width='300px'
                          maxWidth='580px'
                          layout={{grow: 1}}
                          margin='0px 20px 20px 20px'>
        <span dangerouslySetInnerHTML={{__html: sanitizeHtml(brand.marketingMsg)}}/>
      </DescriptiveText>}
    </>;

    return <Page backgroundImage={brand.backgroundImage} minHeight='100vh'>
      <Row alignItems='middle' alignSubRow='center' justifySubRow='center' width='100%' wrap={true}>
        <Row alignItems='middle'
             height='fit-content'
             justifySubRow='center'
             layout={{grow: +displayAdvisorMedia}}
             width={displayAdvisorMedia ? 'fit-content' : '100%'}
             wrap={false}>
          <_Brand height='fit-content'
                  width='fit-content'
                  justifySubColumn='middle'
                  alignItems='center'
                  backgroundColor={this.props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
                  backDropFilter='10px'
                  borderRadius='12px'>
            {!!brand.logo && <Image maxHeight='120px' maxWidth='270px' margin='20px' src={brand.logo}/>}
            {!!marketingMessage && marketingDisplay}
          </_Brand>
        </Row>
        <Row alignItems='middle' justifySubRow='center' height='fit-content' width='fit-content' wrap={false}>
          <Tile title='Login'
                maxWidth={isLogoSpaceShared ? '300px' : '500px'}
                height='fit-content'
                layout={{grow: 1}}>
            <Column showDividers={true} width={'100%'} height={'100%'}>
              <form onSubmit={evt => {
                evt.preventDefault();
                evt.stopPropagation();
                this.setState({processing: true});
                createSession(this.state)
                  .finally(() => this.setState({processing: false}));
              }}>
                <Row wrap={true}>
                  <TextInput
                    dataPoint={{name: 'email', value: email}}
                    title='Email'
                    type='Email'
                    placeholder='Email Address...'
                    onChange={this.updateValue}
                    maxWidth='350px'
                    required>
                  </TextInput>
                  <TextInput
                    dataPoint={{name: 'password', value: password}}
                    title='Password'
                    type='password'
                    placeholder='Password...'
                    onChange={this.updateValue}
                    maxWidth='350px'
                    required>
                  </TextInput>
                </Row>
                <Row wrap={true}>
                  <Button
                    size='md'
                    importance='primary'
                    processing={this.state.processing}
                    label='Log In'
                    width='250px'
                    action='SUBMIT'/>
                </Row>
                <Row wrap={true}>
                  <Column width={'50%'}>
                    <LinkedText size='sm'
                                fontSize='10px'
                                colorize={true}
                                action={openModal({componentName: 'FirstTimeLogin'})}>
                      Don’t have credentials?
                    </LinkedText>
                  </Column>
                  <Column width={'50%'}>
                    <LinkedText size='sm'
                                fontSize='10px'
                                colorize={true}
                                action={openModal({componentName: 'ForgotPassword', email: email})}>
                      Forgot Password?
                    </LinkedText>
                  </Column>
                </Row>
              </form>
              <Row wrap={true}>
                <Column>
                  <Row action={openModal({type: 'SecurityPolicy'})} wrap={false}>
                    <Image src={icons.lock} maxHeight={'30px'} maxWidth={'30px'}/>
                    <DescriptiveText preset={'bigNumber'}>Safe and Secure</DescriptiveText>
                  </Row>
                  <Row wrap={true}>
                    <ContactClientServices alignment={'center'} colorize={false}/>
                  </Row>
                  {enableSocialMedia && <SocialMedia/>}
                </Column>
              </Row>
              <Row wrap={true}>
                {!!brand.poweredByImage && <>
                  <DescriptiveText alignment='left' preset='dataLabel'>Powered By</DescriptiveText>
                  <Row wrap={true}>
                    <Image height='30px' maxHeight='100px' maxWidth='100px' src={brand.poweredByImage}/>
                  </Row>
                </>}
                <Row wrap={true}>
                  <Button importance={'secondary'}
                          size={'sm'}
                          width={'100px'}
                          label={'Disclosures'}
                          action={openModal({
                            type: 'Disclosures',
                            defaultTab: 'privacyPolicy',
                          })}
                  />
                </Row>
                <CopyrightVersion alignment={'center'}/>
              </Row>
            </Column>
          </Tile>
        </Row>
      </Row>
    </Page>;
  }
}

const mapStateToProps = state => ({
  session: state.session,
  product: state.product,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  createSession: credentials => {
    // ownProps.history.push('/');
    localStorage.setItem('email', credentials.email);
    return dispatch(_createSession(credentials, ownProps.prevPath));
  },
  openModal: currentModal => () => {
    dispatch(_openModal(currentModal));
  },
});

export default withTheme(withRouter(connect(mapStateToProps, mapDispatchToProps)(Login)));

Login.propTypes = {
  /**
   * User Session, includes token and email.
   */
  session: PropTypes.shape({
    token: PropTypes.string.isRequired,
    email: PropTypes.string,
  }).isRequired,
  /**
   * Function that creates a session when user logs in
   */
  createSession: PropTypes.func.isRequired,
  /**
   * Function that opens modals throughout page
   */
  openModal: PropTypes.func.isRequired,
  /**
   * The Product
   */
  product: PropTypes.shape({
    brand: PropTypes.object.isRequired,
    version: PropTypes.string,
  }).isRequired,
  theme: PropTypes.object,
};
